import { AfterViewInit, ChangeDetectorRef, Directive, EventEmitter, forwardRef, inject, Injector, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NgControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, filter, map, Observable, skip } from 'rxjs';

type ChangeSource = 'View' | 'Model';

interface LatestChange<T> {
    source: ChangeSource;
    value: T;
}

const mapValue = <T>() => map<LatestChange<T> | undefined, T | undefined>((change) => change?.value);
const filterSource = <T>(changeSource: ChangeSource) => filter<LatestChange<T> | undefined>((change) => change?.source === changeSource);

/**
 * Handles the interaction between Forms and a custom component, by implementing ControlValueAccessor.
 * Implemanted as a Directive for Composition, so it enables composing another capabilities from other directives on
 * the custom component.
 * This Directive facilitates usage by exposes features like direct aceess to: formControl, current value,
 * value changes events from DOM, programmatically or both, and to disable state.
 *
 * Usage:
 *
 * ```ts
 * @Component({
 *     hostDirectives: [ValueAccessorDirective],
 *     ...,
 *     template: `
 *         <input type="text" [value]="valueAccessor.value" (input)="onDomValueChanged($event)"/>
 *     `
 * })
 * export class MyCustomComponent {
 *     ...
 *     protected readonly valueAccessor = injectValueAccessor<string>()
 *     ...
 *
 *     constructor() {
 *         this.valueAccessor.disabledStateChanged.subscribe(...)
 *     }
 *
 *     protected onDomValueChange(newValue: string): void {
 *         this.valueAccessor.setValue(newValue); *
 *     }
 * }
 * ```
 */
@Directive({
    standalone: true,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ValueAccessorDirective),
            multi: true
        }
    ]
})
export class ValueAccessorDirective<T> implements ControlValueAccessor, AfterViewInit, OnDestroy {
    private _control: NgControl | null = null;

    private readonly injector = inject(Injector);
    private readonly changeDetector = inject(ChangeDetectorRef);
    private readonly value$ = new BehaviorSubject<LatestChange<T> | undefined>(undefined);
    /**
     * Emits all changes to the value. This will always reflect the latest value emitted by whether
     * it was triggered by programmatic interaction with the model through `FormControl`/`NgModel` or user interaction's with the DOM.
     */
    public readonly valueChanged = this.value$.pipe(skip(1), mapValue()) as Observable<T>;
    /**
     * Emits changes to the value triggered by user interactions with the DOM.
     */
    public readonly viewValueChanged = this.value$.pipe(filterSource('View'), mapValue());
    /**
     * Emits changes to the value triggered by programmtic interaction with the model through `FormControl` or `NgModel`.
     */
    public readonly modelValueChanged = this.value$.pipe(filterSource('Model'), mapValue());

    public readonly disabledStateChanged = new EventEmitter<boolean>();

    private onChange: (value: T | undefined) => void = () => void 0;
    private onTouched: () => void = () => void 0;

    public ngAfterViewInit(): void {
        this._control = this.injector.get(NgControl, null, { optional: true });
    }

    public ngOnDestroy(): void {
        this.value$.complete();
    }

    // === State getters for DOM reflection in the hosting UI component ===

    public get value(): T | undefined {
        return this.value$.value?.value;
    }

    public get isValid(): boolean {
        return this.control?.valid ?? true;
    }

    public get isInvalid(): boolean {
        return !this.isValid;
    }

    public get control(): NgControl | null {
        return this._control;
    }

    // === API for the hosting UI component to notify Angular ===

    public setValue(value: T): void {
        this.onChange(value);
        this.value$.next({ source: 'View', value });
    }

    public markAsTouched(): void {
        this.onTouched();
    }

    // === ControlValueAccessor API for Angular to interact with the hosting UI component ===

    /**
     * @deprecated THIS IS INTERNAL API CALLED BY ANGULAR. IT IS NOT REALLY DEPRECATED, BUT PLEASE DON'T CALL IT MANUALLY.
     *
     * If you need to programatically change the value of the component call {@link setValue setValue()}.
     */
    public writeValue(value: T): void {
        this.value$.next({ source: 'Model', value });
        this.changeDetector.markForCheck();
    }

    /**
     * @deprecated THIS IS INTERNAL API CALLED BY ANGULAR. IT IS NOT REALLY DEPRECATED, BUT PLEASE DON'T CALL IT MANUALLY.
     */
    public registerOnChange(fn: (value: T | undefined) => void): void {
        this.onChange = fn;
    }

    /**
     * @deprecated THIS IS INTERNAL API CALLED BY ANGULAR. IT IS NOT REALLY DEPRECATED, BUT PLEASE DON'T CALL IT MANUALLY.
     */
    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    /**
     * @deprecated THIS IS INTERNAL API CALLED BY ANGULAR. IT IS NOT REALLY DEPRECATED, BUT PLEASE DON'T CALL IT MANUALLY.
     *
     * If you need to control your form control's disabled state, use the form control object itself.
     */
    public setDisabledState(isDisabled: boolean): void {
        this.disabledStateChanged.emit(isDisabled);
    }
}
